import { useState, useEffect, useRef, type FormEvent } from 'react';
import {
  subscribeToUserThread,
  sendUserMessage,
  markUserRead,
} from '../../lib/adminMessaging';
import type { ThreadMessage } from '../../lib/adminMessaging';

interface Props {
  userId: string;
  explorerName: string;
  onClose: () => void;
}

export default function AdminMessagePanel({ userId, explorerName, onClose }: Props) {
  const [thread, setThread] = useState<ThreadMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    markUserRead(userId).catch(() => {});
    return subscribeToUserThread(userId, msgs => {
      setThread(msgs);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    });
  }, [userId]);

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await sendUserMessage(userId, explorerName, text.trim());
      setText('');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-full rounded-2xl overflow-hidden shadow-2xl bg-white">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ background: '#F59E0B' }}
      >
        <div>
          <p className="text-sm font-bold text-black">Ask Koko</p>
          <p className="text-xs text-black/60">Message the Omore team</p>
        </div>
        <button
          onClick={onClose}
          className="text-black/60 hover:text-black text-lg leading-none"
        >
          ×
        </button>
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" style={{ background: '#F9FAFB' }}>
        {thread.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-6">
            <div className="text-3xl mb-2">👋</div>
            <p className="text-sm font-medium text-gray-700">Hey {explorerName}!</p>
            <p className="text-xs text-gray-500 mt-1 max-w-[180px]">
              Send us a message and we’ll get back to you soon.
            </p>
          </div>
        )}
        {thread.map(msg => (
          <div
            key={msg.id}
            className={`flex ${
              msg.fromAdmin ? 'justify-start' : 'justify-end'
            }`}
          >
            <div
              className="max-w-[220px] rounded-2xl px-3 py-2"
              style={{
                background: msg.fromAdmin ? '#F59E0B' : '#1F2937',
                color: msg.fromAdmin ? '#000' : '#fff',
              }}
            >
              {msg.fromAdmin && (
                <p className="text-xs font-semibold mb-0.5 opacity-70">{msg.senderName}</p>
              )}
              <p className="text-sm">{msg.content}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSend}
        className="flex gap-2 px-3 py-3 border-t border-gray-100 bg-white"
      >
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="rounded-lg px-3 py-1.5 text-sm font-semibold text-black disabled:opacity-40"
          style={{ background: '#F59E0B' }}
        >
          Send
        </button>
      </form>
    </div>
  );
}
